import { inject, Injectable, NgZone } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';

/** A queued chunk fetch. `state` drives concurrency-slot accounting in settle(). */
interface PreloadTask {
    run: () => void;
    /**
     * Ends this route's preload observable without fetching. Used when abandoning: the observable
     * must be completed rather than dropped, or Angular's `concatMap` would wait on it forever and
     * block every later preload cycle.
     */
    skip: () => void;
    state: 'queued' | 'running' | 'settled';
}

/**
 * Preloads every lazy route (both `loadChildren` and `loadComponent`) during browser idle time,
 * so navigation to a lazy route is instant once its chunk has arrived.
 *
 * Replaces QuicklinkStrategy, which only preloaded routes reachable through a `routerLink`
 * visible in the viewport — that missed routes reached via the custom `pcg-link` component
 * (manual `router.navigateByUrl`) and nav items hidden inside collapsed sub-menus, so those
 * chunks were only fetched on click.
 *
 * Fetches are drained through a SHARED, CONCURRENCY-LIMITED QUEUE. This matters because the
 * router hands every eligible route to preload() at once (RouterPreloader collects them with
 * `mergeAll()`, which has no concurrency cap). Scheduling each one independently — the obvious
 * implementation — means every route registers its own requestIdleCallback with its own timeout,
 * and they then all fire together: either drained in a single idle period (each callback returns
 * immediately, since the network wait is async and consumes none of the idle deadline) or forced
 * simultaneously when their identical timeouts expire on a tab that never idles. Either way it is
 * a burst that competes with rendering — the very thing idle scheduling was meant to avoid.
 *
 * The queue makes the fetches self-throttling instead of time-based: a new one starts as soon as a
 * previous one finishes, so it adapts to the actual connection rather than guessing a stagger
 * interval. Only the first drain waits for an idle window — refills are immediate, because
 * re-waiting per wave stretched preloading to minutes on a tab that is rarely truly idle (see
 * scheduleDrain).
 *
 * The cap is deliberately not aggressive. Its real value is that a user's own click is fetched by
 * the router DIRECTLY, bypassing this queue — so it competes with MAX_CONCURRENT speculative
 * streams instead of the entire lazy graph, and the fetch the user is actually waiting on gets a
 * meaningful share of the connection.
 *
 * A failed fetch is warned about and then treated as a non-event rather than propagated, because
 * Angular's preload pipe is subscribed once at startup with no error handler — see the error branch
 * in preload() for why forwarding would disable preloading app-wide.
 *
 * Metered and slow connections are skipped entirely (see isConnectionConstrained), re-tested before
 * every fetch, and given up on for the session if they stay constrained — preloading spends
 * bandwidth to buy latency, which is the wrong trade over a slow link. Two signals feed that
 * decision: the Network Information API where it exists, and otherwise throughput we measure from
 * resource-timing entries the browser records anyway, so Safari and Firefox are covered too.
 *
 * Opt a route out with `data: { preload: false }`.
 */
@Injectable({ providedIn: 'root' })
export class IdlePreloadStrategy implements PreloadingStrategy {

    private readonly zone = inject(NgZone);

    // Chunk fetches allowed in flight at once. Capped so a user's own click — which the router
    // fetches directly, NOT through this queue — competes with a handful of speculative streams
    // rather than the whole lazy graph at once. Not capped lower than this: at 2 the queue took so
    // long to drain that chunks were routinely still missing when the user clicked, which is the
    // slow navigation this strategy exists to prevent.
    private readonly MAX_CONCURRENT = 6;
    // Longest wait before a drain is forced even if the browser never reports an idle period
    // (e.g. a consistently busy tab). Applies per drain, not per route — that is the difference
    // between a trickle and one simultaneous burst.
    private readonly IDLE_TIMEOUT_MS = 5000;
    // Delay used when requestIdleCallback is unavailable (e.g. Safari before 18.4).
    private readonly FALLBACK_DELAY_MS = 2000;
    // How long to wait before re-testing a constrained connection. Paced with a plain timer rather
    // than requestIdleCallback: on an idle tab, idle callbacks fire almost immediately, so re-using
    // them here would spin hundreds of no-op connection checks per minute.
    private readonly CONSTRAINED_RETRY_MS = 10_000;
    // Abandon preloading once the connection has been continuously constrained for this long. A
    // duration, not an attempt count, precisely because retry pacing varies: a count would be
    // exhausted in milliseconds on an idle tab and take minutes on a busy one.
    private readonly CONSTRAINED_GIVE_UP_MS = 60_000;

    // Throughput floor, below which preloading is judged harmful. Sized against the payload: the
    // full lazy graph is roughly 5 MB compressed, so 50 KB/s means about 100 seconds of contention
    // for chunks the user may never open. Set low on purpose — being wrong in this direction merely
    // preloads over a mediocre link, while being wrong the other way reintroduces the slow
    // navigation this strategy exists to fix.
    private readonly THROUGHPUT_FLOOR_BYTES_PER_SEC = 50 * 1024;
    // Minimum evidence required before the observed estimate is trusted. Without a floor, one
    // small or part-cached response could disable preloading for the session.
    private readonly MIN_SAMPLE_BYTES = 100 * 1024;
    private readonly MIN_SAMPLE_COUNT = 3;
    // Once the accumulators pass this, both are halved so recent transfers dominate. Keeps the
    // estimate responsive to a connection that changes instead of averaging over the whole session.
    private readonly SAMPLE_DECAY_BYTES = 4 * 1024 * 1024;

    private readonly queue: PreloadTask[] = [];
    private active = 0;
    private idleHandle: number | undefined;
    private timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    /** When the current run of constrained checks began; undefined whenever the connection is fine. */
    private constrainedSince: number | undefined;
    /** Set once preloading has been given up on for this page's lifetime. */
    private abandoned = false;
    /**
     * Set once the first idle window (or its timeout) has been reached. After that, freed slots are
     * refilled immediately instead of waiting for another idle window — see scheduleDrain().
     */
    private idleWindowReached = false;

    // Rolling throughput evidence, accumulated from resource-timing entries the browser records
    // anyway. No extra requests and no extra bytes — see ensureThroughputObserver().
    private observedBytes = 0;
    private observedMs = 0;
    private observedCount = 0;
    private throughputObserver: PerformanceObserver | undefined;
    private throughputObserverStarted = false;

    preload(
        route: Route
        , load: () => Observable<unknown>
    ): Observable<unknown> {
        if (route.data?.['preload'] === false) { return of(null); }
        if (this.abandoned) { return of(null); }
        this.ensureThroughputObserver();
        // Fast path only — avoids building a queue at all for a user who starts out constrained.
        // drain() re-tests before every fetch, which is what catches a connection that degrades later.
        if (this.isConnectionConstrained()) { return of(null); }

        return new Observable<unknown>(subscriber => {
            let innerSub: Subscription | undefined;

            const task: PreloadTask = {
                state: 'queued'
                , run: () => {
                    task.state = 'running';
                    innerSub = load().subscribe({
                        next: value => subscriber.next(value)
                        // Report the failure, then COMPLETE rather than error. Angular subscribes
                        // its preload pipe once at startup with no error handler and never rebuilds
                        // it (RouterPreloader.setUpPreloading), so forwarding an error there kills
                        // preloading for every remaining route until the page is reloaded. One stale
                        // chunk hash after a deploy would silently disable the whole feature.
                        //
                        // Swallowing the propagation is safe precisely because this fetch is
                        // speculative: if the chunk really is broken, the route re-fetches it on
                        // click and fails loudly there, where it affects the user and where normal
                        // error handling applies. The warn keeps the failure visible and names the
                        // route, which the bare unhandled RxJS error did not.
                        , error: err => {
                            this.settle(task);
                            console.warn(
                                `[IdlePreloadStrategy] Preload failed for route "${route.path ?? '(pathless)'}". `
                                    + `Navigation to it will still load on demand.`
                                , err
                            );
                            subscriber.complete();
                        }
                        // Release the slot before forwarding, so a settled fetch cannot wedge the queue.
                        , complete: () => {
                            this.settle(task);
                            subscriber.complete();
                        }
                    });
                }
                , skip: () => {
                    this.settle(task);
                    subscriber.complete();
                }
            };

            this.queue.push(task);
            this.scheduleDrain();

            return () => {
                innerSub?.unsubscribe();
                this.settle(task);
            };
        });
    }

    /**
     * Marks a task finished: frees the concurrency slot it held, or drops it from the queue if it
     * never started. Idempotent, because a task can settle both from its own completion and from
     * the router unsubscribing.
     */
    private settle(task: PreloadTask) {
        if (task.state === 'settled') { return; }
        const wasRunning = task.state === 'running';
        task.state = 'settled';

        if (wasRunning) { this.active--; }
        else {
            // Still queued (drain() removes a task before running it), so reclaim the slot it
            // would have taken and keep the queue from growing without bound.
            const queueIndex = this.queue.indexOf(task);
            if (queueIndex !== -1) { this.queue.splice(queueIndex, 1); }
        }

        this.scheduleDrain();
    }

    /**
     * Arms a single drain at the next idle moment. At most one handle is outstanding at a time,
     * which is what keeps N queued routes from becoming N timers that expire together.
     */
    private scheduleDrain() {
        if (this.abandoned) { return; }
        if (
            this.idleHandle != null
            || this.timeoutHandle != null
        ) { return; }
        if (
            !this.queue.length
            || this.active >= this.MAX_CONCURRENT
        ) { return; }

        // Only the FIRST drain waits for an idle window; after that, refill freed slots straight
        // away. Waiting again per wave was a real regression: this app has SSE traffic and polling
        // timers, so true idle periods are scarce, and every wave ended up paying the full
        // IDLE_TIMEOUT_MS — which stretched preloading to minutes and left chunks missing on click.
        // Deferring the START until idle is what actually keeps preloading off the critical path;
        // re-deferring each refill bought almost nothing and cost the entire benefit.
        //
        // This recurses (drain -> scheduleDrain -> drain) but terminates: drain()'s loop always ends
        // with the queue empty or the cap reached, and both are guarded above.
        if (this.idleWindowReached) {
            this.drain();
            return;
        }

        // Schedule outside Angular so idle scheduling and chunk fetches don't trip change
        // detection or keep the app from ever reporting as "stable".
        this.zone.runOutsideAngular(() => {
            const idleApi = globalThis as unknown as {
                requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
            };
            if (typeof idleApi.requestIdleCallback === 'function') {
                this.idleHandle = idleApi.requestIdleCallback(
                    () => {
                        this.idleHandle = undefined;
                        this.idleWindowReached = true;
                        this.drain();
                    }
                    , { timeout: this.IDLE_TIMEOUT_MS }
                );
            } else {
                this.timeoutHandle = setTimeout(
                    () => {
                        this.timeoutHandle = undefined;
                        this.idleWindowReached = true;
                        this.drain();
                    }
                    , this.FALLBACK_DELAY_MS
                );
            }
        });
    }

    /** Starts as many queued fetches as the cap allows, then re-arms if work remains. */
    private drain() {
        if (this.abandoned) { return; }

        // Tested before every fetch, not just at enqueue. The queue can take minutes to drain at
        // MAX_CONCURRENT, and a user can lose wifi or enable Data Saver partway through — an
        // enqueue-time check alone would happily keep downloading over the degraded connection.
        if (this.isConnectionConstrained()) {
            this.constrainedSince ??= performance.now();
            if (performance.now() - this.constrainedSince >= this.CONSTRAINED_GIVE_UP_MS) {
                this.abandonForSession();
            } else {
                this.scheduleConstrainedRetry();
            }
            return;
        }
        // Connection recovered, so the give-up budget starts over. Without this reset a user who
        // briefly passed through a dead spot would permanently lose preloading.
        this.constrainedSince = undefined;

        while (
            this.active < this.MAX_CONCURRENT
            && this.queue.length
        ) {
            const task = this.queue.shift()!;
            this.active++;
            task.run();
        }
        this.scheduleDrain();
    }

    /** Re-tests a constrained connection on a fixed interval rather than at idle-callback pace. */
    private scheduleConstrainedRetry() {
        if (
            this.idleHandle != null
            || this.timeoutHandle != null
        ) { return; }

        this.zone.runOutsideAngular(() => {
            this.timeoutHandle = setTimeout(
                () => {
                    this.timeoutHandle = undefined;
                    this.drain();
                }
                , this.CONSTRAINED_RETRY_MS
            );
        });
    }

    /**
     * Stops preloading for the rest of this page's lifetime. Fetches already in flight are left to
     * finish — their bytes are already spent — but nothing further is started, and queued routes are
     * completed so Angular's preload pipe is not left waiting on them.
     */
    private abandonForSession() {
        this.abandoned = true;
        this.cancelScheduledDrain();
        // Nothing will consult the estimate again, so stop accumulating.
        this.throughputObserver?.disconnect();
        this.throughputObserver = undefined;

        console.warn(
            `[IdlePreloadStrategy] Connection has been constrained for over `
                + `${this.CONSTRAINED_GIVE_UP_MS / 1000}s; abandoning route preloading for this session `
                + `to stop competing for bandwidth. Routes will still load on demand.`
        );

        // Detach before skipping: skip() -> settle() looks the task up in this queue, and clearing
        // it first keeps that lookup from mutating the array we are iterating.
        const abandonedTasks = this.queue.splice(0, this.queue.length);
        for (const task of abandonedTasks) { task.skip(); }
    }

    private cancelScheduledDrain() {
        const idleApi = globalThis as unknown as { cancelIdleCallback?: (id: number) => void };
        if (this.idleHandle != null) {
            idleApi.cancelIdleCallback?.(this.idleHandle);
            this.idleHandle = undefined;
        }
        if (this.timeoutHandle != null) {
            clearTimeout(this.timeoutHandle);
            this.timeoutHandle = undefined;
        }
    }

    /**
     * True when speculative fetching is a bad trade for this user. Routes still load on demand —
     * only the speculative fetch is skipped.
     *
     * Two signals, cheapest first. Neither is cached: drain() re-tests before each fetch, which is
     * what makes a connection that degrades mid-drain actually take effect.
     */
    private isConnectionConstrained(): boolean {
        // Fast path — the browser already knows, so no evidence needs gathering. Chromium only.
        if (this.isReportedConstrained()) { return true; }
        // Universal fallback for the browsers the API above does not exist in.
        return this.isObservedThroughputPoor();
    }

    /**
     * The browser's own verdict, via the Network Information API. `effectiveType` is its estimate of
     * observed round-trip time and throughput rather than the physical medium, so bad wifi can
     * report '2g' — which is the signal we want, since what matters is whether preloading will hurt.
     *
     * Accessed defensively off globalThis: this API is Chromium-only (absent in Safari and Firefox,
     * both of which `.browserslistrc` supports) and `navigator` does not exist during prerendering.
     * Returning false there is deliberate — absence of the API is evidence of a browser that does
     * not report, not of a slow connection, so it must not by itself disable preloading. That gap is
     * what isObservedThroughputPoor() covers.
     */
    private isReportedConstrained(): boolean {
        const connection = (globalThis as unknown as {
            navigator?: {
                connection?: {
                    saveData?: boolean;
                    effectiveType?: string;
                };
            };
        }).navigator?.connection;

        if (connection == null) { return false; }
        if (connection.saveData === true) { return true; }
        return connection.effectiveType === 'slow-2g'
            || connection.effectiveType === '2g';
    }

    /**
     * Our own verdict, from throughput actually delivered for our own assets from our own origin.
     * Works in every browser, and is arguably the better signal even where the API above exists,
     * since it measures this app's real transfers rather than a browser-wide rolling estimate.
     *
     * Returns false until MIN_SAMPLE_BYTES / MIN_SAMPLE_COUNT of evidence exists, so an unproven
     * connection is treated as fine rather than assumed bad.
     */
    private isObservedThroughputPoor(): boolean {
        if (
            this.observedCount < this.MIN_SAMPLE_COUNT
            || this.observedBytes < this.MIN_SAMPLE_BYTES
            || this.observedMs <= 0
        ) { return false; }

        const bytesPerSecond = (this.observedBytes / this.observedMs) * 1000;
        return bytesPerSecond < this.THROUGHPUT_FLOOR_BYTES_PER_SEC;
    }

    /**
     * Starts collecting throughput evidence. This is what makes the probe free: the browser already
     * records a PerformanceResourceTiming entry for every script it fetches, so we add no requests
     * and no bytes — we only read measurements that exist regardless.
     *
     * `buffered: true` replays entries recorded before this ran, which means the initial bundle's
     * own transfers (~1.1 MB of scripts, fetched before any preloading begins) usually supply enough
     * evidence to judge the connection before the first speculative byte is spent.
     *
     * Idempotent, and a no-op where PerformanceObserver is unavailable — in which case only the
     * Network Information fast path applies.
     */
    private ensureThroughputObserver() {
        if (this.throughputObserverStarted) { return; }
        this.throughputObserverStarted = true;

        if (typeof PerformanceObserver !== 'function') { return; }

        this.zone.runOutsideAngular(() => {
            try {
                this.throughputObserver = new PerformanceObserver(
                    list => this.recordThroughput(list.getEntries())
                );
                this.throughputObserver.observe({ type: 'resource', buffered: true });
            } catch {
                // Some browsers reject unsupported entry types by throwing. Losing the probe just
                // falls back to the reported-connection fast path, so this is not worth surfacing.
                this.throughputObserver = undefined;
            }
        });
    }

    /** Folds script transfers into the rolling estimate. Non-representative entries are ignored. */
    private recordThroughput(entries: PerformanceEntryList) {
        for (const entry of entries) {
            const resource = entry as PerformanceResourceTiming;
            if (!resource.name?.endsWith('.js')) { continue; }

            // transferSize 0 means it came from cache — instant, and says nothing about the network.
            const bytes = resource.transferSize ?? 0;
            // responseStart..responseEnd is body download only, excluding queueing and connection
            // setup. Using `duration` instead would misread a chunk queued behind others as slow.
            const ms = resource.responseEnd - resource.responseStart;
            if (
                bytes <= 0
                || ms <= 0
            ) { continue; }

            this.observedBytes += bytes;
            this.observedMs += ms;
            this.observedCount++;

            if (this.observedBytes > this.SAMPLE_DECAY_BYTES) {
                this.observedBytes /= 2;
                this.observedMs /= 2;
            }
        }
    }
}
